import { useState } from "react";
import { Link } from "react-router";

import Image from "~/components/ui/image";
import type { HecProduct, HecVariationAttribute } from "~/hec/types/product";
import { buildProductUrl } from "~/hec/utils/product-slug";
import type { C_ProductInfo } from "~/lib/app.types";
import { cn } from "~/lib/ui";
import { getVariantImage } from "~/shop/utils/image";

/**
 * Bundle contained-items list (SFCC-11826) — port of SFRA
 * `org_hec/.../product/components/bundleItems.isml` + `bundleItem.isml`.
 *
 * Two SFRA branches, selected by `variant`:
 * - `pdp` (bundleDetails.isml, isDetail=true): "Includes" heading with a
 *   right-aligned "N item(s)" count; items past the 3rd are collapsed on
 *   mobile behind a "Show all products" toggle.
 * - `quickView` (quickView.isml:165-177, isDetail=false): plain
 *   "Item(s) in this bundle:" label, no count, every item always visible.
 *
 * Per item (both branches): image, name linking to the child's own PDP,
 * variation attribute rows when the child is a variant, and the fixed
 * per-bundle quantity ("Qty: N" — SFRA label.quantity + selectedQuantity).
 * Per-item price is deliberately absent (SFRA suppresses it; only the
 * bundle's own price renders in the buy column).
 *
 * Not ported: SFRA's per-item `mainAttributes` group, size chart, and static
 * option rows — SCAPI exposes none of that data (attribute groups and child
 * option selections aren't in the `bundled_products` payload), and the
 * rating/availability rows SFRA marks `d-none`.
 */

/**
 * Collapse threshold: a bundle with more than this many members truncates
 * behind a Show more/less toggle. Both surfaces use 3 but differ in scope:
 * the PDP collapses mobile-only (SFRA org_hec `bundleDetails.isml`), while the
 * Quick View collapses at all viewports to match the MRT storefront the Workers
 * app replaces (SFCC-11897).
 */
const COLLAPSED_VISIBLE_ITEMS = 3;

type BundleEntry = NonNullable<HecProduct["bundledProducts"]>[number];

// Deliberately not the basket's `mapVariationValuesToRows`. ET-296 taught that
// helper the same `attr.values.find(...).name` resolution this one always did,
// so they now agree on the core lookup — but they are still NOT interchangeable:
//
//  1. `onesize` — the basket helper skips it (cart semantics); this one keeps it.
//  2. Iteration — basket walks `variationValues`; this one walks
//     `variationAttributes`, so rows follow the BM attribute order SFRA's
//     `variationAttribute.isml` uses.
//  3. Shape — `{ id, displayName, value }` vs `{ name, value }`.
//  4. Empty authored name — basket uses `|| String(value)` (falls through to
//     the raw value); this one uses `?? selected` (renders the empty string).
//  5. Row-label fallback — basket capitalizes the attribute id; this one uses
//     the raw `attr.id`.
//  6. Missing `variationAttributes` — this one bails to NO rows; the basket
//     helper still emits capitalized-id rows (pinned by its unit test).
//
// (6) is the one that would bite a naive merge. Worth consolidating behind one
// helper with a flag, but that's its own ticket.
function variationRows(
  child: HecProduct,
): Array<{ name: string; value: string }> {
  const values = child.variationValues as Record<string, string> | undefined;
  const attrs = child.variationAttributes as
    | HecVariationAttribute[]
    | undefined;
  if (!values || !attrs) return [];
  return attrs.flatMap(attr => {
    const selected = values[attr.id];
    if (!selected) return [];
    const display =
      attr.values?.find(v => v.value === selected)?.name ?? selected;
    return [{ name: attr.name ?? attr.id, value: display }];
  });
}

function BundleItemCard({
  entry,
  hiddenWhenCollapsed,
  collapseAllViewports,
  onItemLinkClick,
}: {
  entry: BundleEntry;
  hiddenWhenCollapsed: boolean;
  /** Quick view hides overflow members at every width; PDP hides mobile-only. */
  collapseAllViewports: boolean;
  onItemLinkClick?: () => void;
}) {
  const child = entry.product;
  const image = getVariantImage(child as unknown as C_ProductInfo)?.[0];
  return (
    <div
      data-testid={`pdp-bundle-item-${child.id}`}
      className={cn(
        "flex w-full gap-4 border-b border-[#ccc] py-4",
        hiddenWhenCollapsed &&
          (collapseAllViewports
            ? "hidden"
            : // 768px, not this repo's md (828px): SFRA's collapse is
              // bootstrap-md gated, and the sibling badges row pins the same
              // boundary.
              "min-[768px]:flex hidden"),
      )}
    >
      {image?.link && (
        // Route through the shared <Image> so SCAPI's raw instance-host link
        // (`*.commercecloud.salesforce.com`, which 403s + ORB-blocks) is
        // re-pointed at IMAGES_HOST — same fix as the cart bundle thumbnails
        // (SFCC-11868). A plain <img src={image.link}> renders broken on any
        // env where SCAPI returns the instance host (SFCC-11982).
        <Image
          src={image.link}
          alt={child.name ?? ""}
          className="h-24 w-24 shrink-0 object-contain"
          loading="lazy"
        />
      )}
      <div className="flex min-w-0 flex-col gap-1 text-sm">
        <Link
          to={buildProductUrl(child.id, child.name)}
          onClick={onItemLinkClick}
          className="font-medium text-emphasis hover:underline"
        >
          {child.name}
        </Link>
        {variationRows(child).map(row => (
          <span key={row.name} className="text-foreground">
            {row.name}: {row.value}
          </span>
        ))}
        <div className="text-foreground">
          <span className="font-medium">Qty:</span> {entry.quantity}
        </div>
      </div>
    </div>
  );
}

export function BundleItems({
  product,
  variant,
  onItemLinkClick,
}: {
  product: HecProduct;
  variant: "pdp" | "quickView";
  /** Quick view passes its close handler so tapping a child link dismisses the modal. */
  onItemLinkClick?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const entries = (product.bundledProducts ?? []).filter(e => e?.product?.id);
  if (entries.length === 0) return null;

  const collapsible = entries.length > COLLAPSED_VISIBLE_ITEMS;
  // PDP mirrors SFRA (collapse on mobile only, desktop shows all); Quick View
  // mirrors the MRT storefront — collapse at all viewports (SFCC-11897).
  const collapseAllViewports = variant === "quickView";
  const listId = `bundle-items-list-${product.id}`;

  return (
    <section
      data-testid="pdp-bundle-items"
      aria-label="Bundle items"
      className="mt-6 w-full px-5 sm:px-0"
    >
      {variant === "pdp" ? (
        <div className="flex items-baseline justify-between border-b border-[#ccc] pb-2">
          <h2 className="text-md font-grotesque font-medium text-emphasis">
            Includes
          </h2>
          <div>
            <span className="text-md font-medium text-foreground">
              {entries.length}
            </span>{" "}
            <span className="text-md text-foreground">
              {entries.length === 1 ? "item" : "items"}
            </span>
          </div>
        </div>
      ) : (
        <div className="border-b border-[#ccc] pb-2 text-sm font-medium text-[#375481]">
          {entries.length === 1
            ? "Item in this bundle:"
            : "Items in this bundle:"}
        </div>
      )}

      <div id={listId}>
        {entries.map((entry, index) => (
          <BundleItemCard
            key={entry.product.id}
            entry={entry}
            hiddenWhenCollapsed={
              collapsible && !expanded && index >= COLLAPSED_VISIBLE_ITEMS
            }
            collapseAllViewports={collapseAllViewports}
            onItemLinkClick={onItemLinkClick}
          />
        ))}
      </div>

      {collapsible && (
        <button
          type="button"
          className={cn(
            "mt-3 text-sm font-medium underline",
            // PDP toggle is mobile-only (desktop shows every member); Quick
            // View toggle shows at all viewports (SFCC-11897).
            !collapseAllViewports && "min-[768px]:hidden",
          )}
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => setExpanded(v => !v)}
        >
          {collapseAllViewports
            ? expanded
              ? "Show Less Products"
              : "Show More Products"
            : expanded
              ? "Show less products"
              : "Show all products"}
        </button>
      )}
    </section>
  );
}
